import React, { useState } from 'react';
import { t } from '../../../../../helpers/i18n';
import './CreateProjectForm.scss';
import { Form, Input, Space, Typography } from 'antd';
import { EyeFilled } from '@ant-design/icons';
import UploadLogo from '../UploadLogo/UploadLogo';
import { UndefinedAble } from '../../../../../data/base';
import RemoteConfigTable from '../RemoteConfigTable/RemoteConfigTable';
import AppFooter from '../AppFooter/AppFooter';
import { useHistory } from 'react-router-dom';
import { RcFile } from 'antd/es/upload/interface';
import { error, Result, success } from 'util/result';
import { hideLoading, showLoading } from '../../../../../store/loading/actions';
import { useDispatch, useSelector } from 'react-redux';
import FileService from '../../../../../services/apis/file/FileService';
import { showError } from '../../../../../store/popup/actions';
import { Product } from '../../../../../data/terra/Product';
import { mapProjectToProduct } from '../../../../../util/projectMapper';
import { isValidProjectName } from '../../../../util/project';
import ProjectConfigJsonView from '../JsonView/ProjectConfigJsonView';
import { fetchProjects } from 'v2/store/projectList/actions/FetchProjects';
import { RootState } from '../../../../../store/reducer';
import ReadOnlyTextView from '../../../../components/display/ReadOnlyTextView';
import TerraBff from 'services/apis/terraBff/TerraBff';

const { Link } = Typography;

export enum RemoteConfigMode {
  JSON,
  TABLE,
}

function isJsonMode(configMode: RemoteConfigMode): boolean {
  return configMode === RemoteConfigMode.JSON;
}

interface CreateProjectProps {
  onCreateProjectSuccess: (project: Product) => void;
}

const CreateProjectForm: React.FC<CreateProjectProps> = ({
  onCreateProjectSuccess,
}) => {
  const { selectedOrganization } = useSelector(
    (state: RootState) => state.organizationList
  );
  const [remoteConfigMode, setRemoteConfigMode] = useState(
    RemoteConfigMode.JSON
  );
  const [extraConfig, setExtraConfig] = useState<any>();
  const [logoFile, setLogoFile] = useState<UndefinedAble<RcFile>>();
  const [projectName, setProjectName] = useState<string>('');
  const [projectNameError, setProjectNameError] = useState<string>('');

  const [jsonExtraConfigText, setConfigText] = useState<
    UndefinedAble<string>
  >();
  const [jsonError, setJsonError] = useState<string>('');
  const history = useHistory();
  const dispatch = useDispatch();

  function switchRemoteConfigMode() {
    setRemoteConfigMode(
      isJsonMode(remoteConfigMode)
        ? RemoteConfigMode.TABLE
        : RemoteConfigMode.JSON
    );
  }

  const onJsonDataChanged = (extra: any) => {
    const str = JSON.stringify(extra);
    if (str !== jsonExtraConfigText) {
      setConfigText(str);
      setExtraConfig(extra);
      setJsonError('');
    }
  };

  const onConfigTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (jsonExtraConfigText !== event.target.value) {
      setConfigText(event.target.value);

      try {
        const parsed = JSON.parse(event.target.value);
        if (!parsed || typeof parsed !== 'object') {
          setInputJsonErrorAndReset(t('Invalid JSON'));
          return;
        }
        for (const key in parsed) {
          if (typeof parsed[key] !== 'string') {
            setInputJsonErrorAndReset(t('Error accept JSON string map only'));
            return;
          }
        }

        setJsonError('');
        setExtraConfig(parsed);
      } catch (e) {
        setInputJsonErrorAndReset(t('Invalid JSON'));
      }
    }
  };

  const setInputJsonErrorAndReset = (error: string) => {
    setJsonError(error);
    setExtraConfig({});
  };

  function checkProjectNameError(name: string): boolean {
    if (!name) {
      setProjectNameError(t('create_project_project_name_empty_error'));
      return true;
    }

    if (!isValidProjectName(name)) {
      setProjectNameError(t('create_project_project_name_invalid_error'));
      return true;
    }

    setProjectNameError('');
    return false;
  }

  const onProjectNameTextChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const name = event.target.value;
    setProjectName(name);
    checkProjectNameError(name);
  };

  const onCancel = () => {
    history.goBack();
  };

  const onCreateNew = async () => {
    if (checkProjectNameError(projectName)) {
      return;
    }

    if (jsonError) {
      return;
    }

    const organizationId = selectedOrganization?.id;
    if (!organizationId) {
      return;
    }

    dispatch(showLoading());

    const uploadFileResult = await uploadFileIfNeeded();
    if (uploadFileResult.isError()) {
      dispatch(hideLoading());
      dispatch(showError(t('create_project_error')));
      return;
    }

    const logoUrl = uploadFileResult.data;

    const createProjectResult = await TerraBff.project.addProject({
      projectName: projectName,
      organizationId: organizationId,
      logoUrl: logoUrl ?? '',
      extraConfig: extraConfig,
    });

    dispatch(hideLoading());

    if (createProjectResult.isSuccess()) {
      refreshProjectsAfterCreatingNew();
      onCreateProjectSuccess(
        mapProjectToProduct(createProjectResult.data?.data!)
      );
    } else {
      dispatch(showError(t('create_project_error')));
    }
  };

  const refreshProjectsAfterCreatingNew = () => {
    dispatch(fetchProjects());
  };

  async function uploadFileIfNeeded(): Promise<Result<string>> {
    if (!logoFile) return success('');

    const formData = new FormData();
    formData.append('file', logoFile);
    const result = await FileService.fileApis.uploadImage(formData);

    if (!result.isSuccess()) {
      return error(result.exception());
    }

    return success(result.data?.url ?? '');
  }

  const onFileChange = (file: UndefinedAble<RcFile>) => {
    setLogoFile(file);
  };

  const onTableConfigChanged = (newConfig: any) => {
    const str = JSON.stringify(newConfig);
    if (str !== jsonExtraConfigText) {
      setConfigText(str);
      setExtraConfig(newConfig);
      setJsonError('');
    }
  };

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <div
        id="create-project-form-container"
        className={'create-project-form-container'}
      >
        <p className="create-project-add-project-style">
          {t('create_project_your_project')}
        </p>

        <h2 className="create-project-form-title">
          {t('create_project_add_new_project')}
        </h2>

        <div style={{ marginBottom: '24px' }}>
          <Space align="baseline">
            <p className="create-project-section-title">
              {t('create_project_common_config')}
            </p>
            <p className="create-project-required-text-style">*</p>
          </Space>

          <div className="create-project-common-information-container">
            <div style={{ flex: 1, marginRight: 8 }}>
              <p className="create-project-field-title">
                {t('create_project_project_name_title')}
              </p>
              <Form.Item
                validateStatus={projectNameError ? 'error' : 'success'}
                help={projectNameError}
              >
                <Input
                  size="large"
                  placeholder={t('create_project_project_name_placeholder')}
                  maxLength={100}
                  onChange={onProjectNameTextChange}
                  status={projectNameError ? 'error' : ''}
                />
              </Form.Item>
            </div>

            <p className="create-project-field-title">
              {t('create_project_project_id_title')}
            </p>
            <Input
              disabled={true}
              size="large"
              placeholder={t('create_project_project_id_placeholder')}
            />

            <p className="create-project-field-title">
              {t('create_project_project_logo')}
            </p>

            <UploadLogo onFileChange={onFileChange} imageUrl={undefined} />
            <p className="create-project-field-title">
              {t('create_project_organization')}
            </p>
            <ReadOnlyTextView
              value={selectedOrganization?.name ?? ''}
              label={''}
            />
          </div>
        </div>

        <div className="create-project-config-title-container">
          <p className="create-project-section-title">
            {t('create_project_declare_remote_config')}
          </p>
          <div>
            <div
              onClick={switchRemoteConfigMode}
              className="create-project-config-mode-container"
            >
              <EyeFilled
                style={{ color: '#1990FF', fontSize: 18, marginRight: '8px' }}
              />
              <Link>
                {t(
                  isJsonMode(remoteConfigMode)
                    ? 'create_project_view_table_mode'
                    : 'create_project_view_json_mode'
                )}
              </Link>
            </div>
          </div>
        </div>
        <div className="create-project-common-information-container">
          <p className="create-project-field-title">
            {t('create_project_json_input_title')}
          </p>
          <Input
            value={jsonExtraConfigText}
            size="large"
            placeholder={t('create_project_json_input_placeholder')}
            onChange={onConfigTextChange}
          />
          {jsonExtraConfigText && (
            <Typography.Text type="danger">{jsonError}</Typography.Text>
          )}

          {isJsonMode(remoteConfigMode) && (
            <p className="create-project-field-title">
              {t('create_project_json_viewer_title')}
            </p>
          )}
          {isJsonMode(remoteConfigMode) && (
            <div className="create-project-json-container">
              <ProjectConfigJsonView
                onJsonDataChanged={onJsonDataChanged}
                extraConfig={extraConfig}
              />
            </div>
          )}
          {!isJsonMode(remoteConfigMode) && (
            <RemoteConfigTable
              extraConfig={extraConfig}
              onTableConfigChanged={onTableConfigChanged}
            />
          )}
        </div>
      </div>
      <AppFooter onCancel={onCancel} onCreateNew={onCreateNew} />
    </div>
  );
};

export default CreateProjectForm;
